/**
 * Shared display-title logic for activity rows (issue #454).
 *
 * Both halves of the contact page render the same activity records:
 *   - the Activities tab, via ActivityCard's collapsed header
 *   - the Recent Activity panel in the right rail
 *
 * They must agree on the text for a given record, so the "lead with the
 * title, fall back to the capitalized type" rule lives here rather than
 * being re-derived at each call site.
 *
 * A title that is blank OR whitespace-only counts as absent — the row falls
 * back to the capitalized type and callers suppress the secondary type label
 * (via `hasTitle`) so the row never reads "Task / Task".
 */
export function activityDisplay(activity) {
    // An entry from an event's activity log has no title of its own: the rail reads "<actor> <what they did>" (#1013).
    const eventSentence = activity?.type === 'event_activity' && activity?.text
        ? `${activity.actor_kind === 'system' ? 'Automatically' : (activity.actor_name || 'Someone')} ${activity.text}` : '';
    const rawTitle = (activity?.title || eventSentence || '').trim();
    const hasTitle = rawTitle.length > 0;
    const type = activity?.type || 'note';
    // `external_event` reads "External event", not "External_event" (#797).
    const typeText = type.replace(/_/g, ' ');
    const typeLabel = typeText.charAt(0).toUpperCase() + typeText.slice(1);
    // An event an app reported through the public API names the API key it came
    // from; the timeline sends it as `source_name` because `source` there is the
    // row kind ("activity" / "communication"), not where the record came from.
    const sourceName = (activity?.type === 'external_event' && String(activity?.source_name || '').trim()) || '';
    return { rawTitle, hasTitle, type, typeText, typeLabel, sourceName, displayTitle: hasTitle ? rawTitle : typeLabel };
}

/**
 * An external event's properties as display rows, in the order the app sent
 * them. A nested object or array is shown as compact JSON — properties are
 * capped at 3 levels and 8 KB by the public API, so this stays readable.
 */
export function eventPropertyRows(properties) {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return [];
    return Object.entries(properties).map(([key, value]) => ({
        key,
        value: value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value),
    }));
}

export default activityDisplay;
